const PARTNER_API_BASE = '/api/erp/v1/partners';
const PARTNER_DEVICE_KEY = 'pontomais.partner.device.v1';
const PARTNER_CSRF_KEY = 'pontomais.partner.csrf.v1';
const AFFILIATE_REFERRAL_KEY = 'nivora.affiliate.referral.v1';

let currentSession = null;

export class PartnerApiError extends Error {
  constructor(message, { status = 0, code = 'partner_api_error', details = null } = {}) {
    super(message);
    this.name = 'PartnerApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_error) {
    return null;
  }
}

function randomDeviceId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `partner-web-${crypto.randomUUID()}`;
  }
  return `partner-web-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}

export function getPartnerDeviceId() {
  const storage = getSessionStorage();
  const saved = storage?.getItem(PARTNER_DEVICE_KEY);
  if (saved && saved.length >= 8 && saved.length <= 200) return saved;
  const created = randomDeviceId();
  storage?.setItem(PARTNER_DEVICE_KEY, created);
  return created;
}

function normalizeSession(payload) {
  const data = payload?.data || payload || {};
  const accessToken = data.accessToken || data.access_token || data.session?.accessToken || null;
  const csrfToken = data.csrfToken || data.csrf_token || data.session?.csrfToken || null;
  const partner = data.partner || data.user || data.actor?.partner || data.actor || null;
  const heartbeatIntervalMs = Number(data.heartbeatIntervalMs || data.heartbeat_interval_ms || 0) || null;
  if (!accessToken) return null;
  return { accessToken, csrfToken, partner, heartbeatIntervalMs };
}

function persistSession(session) {
  currentSession = session;
  const storage = getSessionStorage();
  if (!session?.csrfToken) {
    storage?.removeItem(PARTNER_CSRF_KEY);
    return;
  }
  storage?.setItem(PARTNER_CSRF_KEY, session.csrfToken);
}

function readPersistedSession() {
  return currentSession?.accessToken ? currentSession : null;
}

function responseMessage(payload, fallback) {
  return payload?.error?.message
    || payload?.message
    || payload?.error_description
    || fallback;
}

async function parseResponse(response) {
  const contentType = String(response.headers?.get?.('content-type') || '').toLowerCase();
  if (!contentType.includes('application/json')) return null;
  try {
    return await response.json();
  } catch (_error) {
    return null;
  }
}

async function request(path, {
  method = 'GET',
  body,
  authenticated = false,
  retryAfterRefresh = true,
  extraHeaders = null
} = {}) {
  const session = authenticated ? readPersistedSession() : currentSession;
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (authenticated && session?.accessToken) headers.Authorization = `Bearer ${session.accessToken}`;
  if (authenticated && session?.csrfToken) headers['X-PontoMais-Partner-CSRF'] = session.csrfToken;
  if (authenticated) headers['X-PontoMais-Partner-Device-Id'] = getPartnerDeviceId();
  Object.assign(headers, extraHeaders || {});

  let response;
  try {
    response = await fetch(`${PARTNER_API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'include'
    });
  } catch (_error) {
    throw new PartnerApiError(
      'O portal de parceiros esta temporariamente indisponivel. Tente novamente em alguns instantes.',
      { code: 'network_unavailable' }
    );
  }

  const payload = await parseResponse(response);
  if (response.status === 401 && authenticated && retryAfterRefresh) {
    try {
      await refreshPartnerSession();
      return request(path, { method, body, authenticated, retryAfterRefresh: false, extraHeaders });
    } catch (_error) {
      clearPartnerSession();
    }
  }

  if (!response.ok || payload?.ok === false) {
    const code = (typeof payload?.error === 'string' ? payload.error : payload?.error?.code)
      || payload?.code
      || `http_${response.status}`;
    throw new PartnerApiError(
      responseMessage(payload, response.status >= 500
        ? 'O portal de parceiros esta temporariamente indisponivel.'
        : 'Nao foi possivel concluir a solicitacao.'),
      { status: response.status, code, details: payload?.error?.details || payload?.details || null }
    );
  }
  return payload?.data ?? payload;
}

export function clearPartnerSession() {
  persistSession(null);
}

export function hasPartnerSession() {
  return Boolean(readPersistedSession()?.accessToken);
}

export function hasRecoverablePartnerSession() {
  return hasPartnerSession() || Boolean(getSessionStorage()?.getItem(PARTNER_CSRF_KEY));
}

export async function submitPartnerApplication(application, { idempotencyKey } = {}) {
  const key = String(idempotencyKey || `partner-application-${getPartnerDeviceId()}-${Date.now()}`).trim();
  return request('/applications', {
    method: 'POST',
    body: application,
    extraHeaders: { 'Idempotency-Key': key }
  });
}

function getLocalStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_error) {
    return null;
  }
}

export function normalizeAffiliateCode(value) {
  const code = String(value || '').normalize('NFKC').trim().toUpperCase();
  return /^[A-Z0-9-]{8,40}$/.test(code) ? code : '';
}

export function getStoredAffiliateCode() {
  return normalizeAffiliateCode(getLocalStorage()?.getItem(AFFILIATE_REFERRAL_KEY));
}

export async function captureAffiliateReferral(codeValue, { sourcePath } = {}) {
  const code = normalizeAffiliateCode(codeValue);
  if (!code) return null;
  const result = await request('/track', {
    method: 'POST',
    body: {
      code,
      sourcePath: String(sourcePath || (typeof window !== 'undefined' ? window.location.pathname : '/')).slice(0, 300)
    }
  });
  const confirmed = normalizeAffiliateCode(result?.code);
  if (confirmed) getLocalStorage()?.setItem(AFFILIATE_REFERRAL_KEY, confirmed);
  return result;
}

export function captureAffiliateReferralFromLocation(locationLike = typeof window !== 'undefined' ? window.location : {}) {
  const direct = new URLSearchParams(String(locationLike?.search || '').replace(/^\?/, '')).get('ref');
  const hash = String(locationLike?.hash || '');
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  const fromHash = new URLSearchParams(hashQuery).get('ref');
  const code = normalizeAffiliateCode(direct || fromHash);
  if (!code) return Promise.resolve(null);
  return captureAffiliateReferral(code, { sourcePath: locationLike?.pathname || '/' });
}

export async function loginPartner({ email, password }) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: {
      email: String(email || '').trim().toLowerCase(),
      password,
      deviceId: getPartnerDeviceId(),
      deviceLabel: typeof navigator === 'undefined' ? 'Navegador' : navigator.userAgent.slice(0, 120),
      clientKind: 'web'
    }
  });
  const session = normalizeSession(data);
  if (!session) throw new PartnerApiError('O servidor nao devolveu uma sessao de parceiro valida.', { code: 'invalid_session_response' });
  persistSession(session);
  return session.partner || data;
}

export async function refreshPartnerSession() {
  const previous = readPersistedSession();
  const csrfToken = previous?.csrfToken || getSessionStorage()?.getItem(PARTNER_CSRF_KEY);
  const headers = {
    Accept: 'application/json',
    'X-PontoMais-Partner-Device-Id': getPartnerDeviceId()
  };
  if (csrfToken) headers['X-PontoMais-Partner-CSRF'] = csrfToken;

  let response;
  try {
    response = await fetch(`${PARTNER_API_BASE}/auth/refresh`, {
      method: 'POST',
      headers,
      credentials: 'include'
    });
  } catch (_error) {
    throw new PartnerApiError('Nao foi possivel renovar a sessao do parceiro.', { code: 'network_unavailable' });
  }
  const payload = await parseResponse(response);
  if (!response.ok || payload?.ok === false) {
    clearPartnerSession();
    throw new PartnerApiError(responseMessage(payload, 'Sua sessao expirou. Entre novamente.'), {
      status: response.status,
      code: (typeof payload?.error === 'string' ? payload.error : payload?.error?.code)
        || payload?.code
        || 'session_expired'
    });
  }
  const session = normalizeSession(payload);
  if (!session) {
    clearPartnerSession();
    throw new PartnerApiError('Sua sessao expirou. Entre novamente.', { code: 'invalid_session_response' });
  }
  persistSession(session);
  return session.partner || payload?.data || payload;
}

export async function restorePartnerSession() {
  if (!hasPartnerSession()) {
    try {
      await refreshPartnerSession();
    } catch (error) {
      if (error.status === 401 || error.code === 'session_expired' || error.code === 'invalid_refresh') return null;
      throw error;
    }
  }
  try {
    const actor = await request('/me', { authenticated: true });
    const partner = actor?.partner || actor;
    currentSession = { ...readPersistedSession(), partner };
    persistSession(currentSession);
    return actor;
  } catch (error) {
    if (error.status === 401 || error.code === 'session_expired') clearPartnerSession();
    throw error;
  }
}

export async function logoutPartner() {
  try {
    if (!hasPartnerSession() && hasRecoverablePartnerSession()) {
      await refreshPartnerSession();
    }
    if (hasPartnerSession()) {
      await request('/auth/logout', {
        method: 'POST',
        authenticated: true,
        retryAfterRefresh: false
      });
    }
  } finally {
    clearPartnerSession();
  }
}

export function getPartnerReferrals() {
  return request('/referrals', { authenticated: true });
}

export function getPartnerDashboard() {
  return request('/dashboard', { authenticated: true });
}

export function requestPartnerWithdrawal(amountCents, note = '', { idempotencyKey } = {}) {
  const key = String(idempotencyKey || `partner-withdrawal-${getPartnerDeviceId()}-${Date.now()}`).trim();
  return request('/withdrawals', {
    method: 'POST',
    authenticated: true,
    body: { amountCents, note },
    extraHeaders: { 'X-Idempotency-Key': key }
  });
}

export function getPartnerIntegrations() {
  return request('/integrations', { authenticated: true });
}

export function createPartnerIntegration(input, { idempotencyKey } = {}) {
  const key = String(idempotencyKey || `partner-integration-create-${getPartnerDeviceId()}-${Date.now()}`).trim();
  return request('/integrations', {
    method: 'POST',
    authenticated: true,
    body: input,
    extraHeaders: { 'X-Idempotency-Key': key }
  });
}

export function updatePartnerIntegration(integrationId, input, { idempotencyKey } = {}) {
  const key = String(idempotencyKey || `partner-integration-update-${getPartnerDeviceId()}-${Date.now()}`).trim();
  return request(`/integrations/${encodeURIComponent(integrationId)}`, {
    method: 'PUT',
    authenticated: true,
    body: input,
    extraHeaders: { 'X-Idempotency-Key': key }
  });
}

export function revokePartnerIntegration(integrationId, { idempotencyKey } = {}) {
  const key = String(idempotencyKey || `partner-integration-revoke-${getPartnerDeviceId()}-${Date.now()}`).trim();
  return request(`/integrations/${encodeURIComponent(integrationId)}`, {
    method: 'DELETE',
    authenticated: true,
    extraHeaders: { 'X-Idempotency-Key': key }
  });
}

export function getPartnerIntegrationHealth(integrationId) {
  return request(`/integrations/${encodeURIComponent(integrationId)}/health`, { authenticated: true });
}

export function heartbeatPartnerSession() {
  return request('/auth/heartbeat', { method: 'POST', authenticated: true });
}

export function getPartnerHeartbeatInterval() {
  const configured = Number(currentSession?.heartbeatIntervalMs || 0);
  return configured > 0 ? Math.max(60_000, configured) : 10 * 60_000;
}

export const __partnerApiTestUtils = {
  PARTNER_API_BASE,
  PARTNER_DEVICE_KEY,
  PARTNER_CSRF_KEY,
  AFFILIATE_REFERRAL_KEY,
  normalizeSession,
  dropMemorySession() {
    currentSession = null;
  },
  reset() {
    currentSession = null;
    const storage = getSessionStorage();
    storage?.removeItem(PARTNER_DEVICE_KEY);
    storage?.removeItem(PARTNER_CSRF_KEY);
    getLocalStorage()?.removeItem(AFFILIATE_REFERRAL_KEY);
  }
};
